import {
  Modal as NextUIModal,
  type ModalProps as NextUIModalProps,
  ModalBody as NextUIModalBody,
  type ModalBodyProps as NextUIModalBodyProps,
  ModalContent as NextUIModalContent,
  type ModalContentProps as NextUIModalContentProps,
  ModalFooter as NextUIModalFooter,
  type ModalFooterProps as NextUIModalFooterProps,
  ModalHeader as NextUIModalHeader,
  type ModalHeaderProps as NextUIModalHeaderProps,
} from '@heroui/modal';
import { type SlotsToClasses, forwardRef } from '@heroui/react';
import { XIcon } from '@phosphor-icons/react';
import useIsEmbed from '@embed/useIsEmbed';

interface ModalProps extends NextUIModalProps {
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  disableOuterScroll?: false | true;
  theme?: string;
  customClassNames?: SlotsToClasses<'backdrop' | 'base' | 'wrapper' | 'header' | 'body' | 'footer' | 'closeButton'> | undefined,
  width?: string;
  height?: string;
}

export const Modal = forwardRef(
  ({
    size = 'md',
    placement = 'top',
    disableOuterScroll,
    theme = '',
    customClassNames,
    classNames,
    width,
    height = '',
    isOpen,
    scrollBehavior,
    ...props
  }: ModalProps, ref) => {
    const isEmbed = useIsEmbed();

    const sizes = {
      sm: 'w-110',
      md: 'w-150',
      lg: 'w-200',
      xl: 'w-300',
      full: 'w-full',
    }[size];

    // In embeds the slide is shrunk via CSS `zoom` on #ppt-stage; the modal portals
    // to document.body (outside that zoom) and renders at native scale so text
    // stays readable regardless of how far the host shrinks the slide. Fit comes
    // from clamping instead of scaling — `.embed-modal-fit` (globals.css) caps the
    // box to the real pane — plus compact chrome (tighter padding, smaller header)
    // so the modal doesn't dwarf a small pane.
    const resolvedClassNames = classNames || customClassNames || {
      wrapper: `${disableOuterScroll && 'overflow-y-hidden'}`,
      backdrop: 'bg-black/75',
      base: `${theme} shadow-[0_25px_50px_-12px_rgba(0,0,0,0.25)] rounded-lg ${isEmbed ? 'p-5' : 'p-7.5'} rounded-xl max-w-full !bg-content2 text-content2-foreground ${sizes} ${height} ${width}`,
      header: `p-0 ${isEmbed ? 'text-lg' : 'text-xl'}`,
      body: 'p-0 pt-2 gap-0',
      footer: 'p-0 pt-4',
      closeButton:
        'p-3 hover:bg-transparent text-content2-foreground rounded-none active:bg-transparent',
    };

    const finalClassNames = isEmbed
      ? { ...resolvedClassNames, base: `${resolvedClassNames.base || ''} embed-modal-fit`.trim() }
      : resolvedClassNames;

    return (
      <NextUIModal
        classNames={finalClassNames}
        motionProps={{
          variants: {
            enter: {
              y: 0,
              opacity: 1,
              transition: {
                duration: 0.2,
                ease: 'easeOut',
              },
            },
            exit: {
              y: 20,
              opacity: 0,
              transition: {
                duration: 0.1,
                ease: 'easeIn',
              },
            },
          },
        }}
        closeButton={
          <div>
            <XIcon className='cursor-pointer text-default-500 hover:text-default-900' size={24} />
          </div>
        }
        placement={placement}
        // Embeds default to inside scroll so a clamped modal scrolls its body
        // with header/footer pinned, rather than clipping at the pane edge.
        scrollBehavior={scrollBehavior ?? (isEmbed ? 'inside' : undefined)}
        isOpen={isOpen}
        ref={ref}
        {...props}
      />
    );
  }
);

interface ModalBodyProps extends NextUIModalBodyProps { }

export const ModalBody = forwardRef((props: ModalBodyProps, ref) => {
  return <NextUIModalBody ref={ref} {...props} />;
});

interface ModalContentProps extends NextUIModalContentProps { }

export const ModalContent = forwardRef((props: ModalContentProps, ref) => {
  return <NextUIModalContent {...props} />;
});

interface ModalFooterProps extends NextUIModalFooterProps { }

export const ModalFooter = forwardRef((props: ModalFooterProps, ref) => {
  return <NextUIModalFooter ref={ref} {...props} />;
});

interface ModalHeaderProps extends NextUIModalHeaderProps { }

export const ModalHeader = forwardRef((props: ModalHeaderProps, ref) => {
  return <NextUIModalHeader ref={ref} {...props} />;
});
